import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AppService } from "../app.service";
import { CcVars } from "./custom-consensus.model";
import dayjs from "dayjs";

@Injectable({
  providedIn: "root",
})
export class CustomConsensusService {
  isCcModalOpen$ = new BehaviorSubject(false);
  isCcNodataModalOpen$ = new BehaviorSubject(false);
  relativeDays$ = new BehaviorSubject(0);
  apiUrl: string = window.apiUrl;

  constructor(
    private http: HttpClient,
    private appService: AppService,
  ) {}

  showCcModal = (state: boolean) => {
    this.isCcModalOpen$.next(state);
  };

  showCcNodataModal = (state: boolean) => {
    this.isCcNodataModalOpen$.next(state);
  };

  shouldShowCcNodataModal = () => {
    const medata = this.appService.superClone(window.MEData);
    const sourcePanel = this.getMEDataPanel(medata, "sources");
    if (sourcePanel) {
      if (
        sourcePanel.custom_consensus_mode !== "byDate" ||
        !sourcePanel.relative_days
      ) {
        this.showCcNodataModal(false);
        return;
      }

      this.relativeDays$.next(sourcePanel.relative_days);
      let refDate = sourcePanel.date_custom_consensus;
      if (sourcePanel.relative_days) {
        refDate = new Date(
          new Date().setDate(new Date().getDate() - sourcePanel.relative_days),
        );
        refDate = dayjs(refDate).format("YYYYMMDD");
      }
      const ticker = window.MEData.ticker;
      const earnings =
        1 - Number(Boolean(!sourcePanel.inc_non_current_brokers));

      this.getTimeBasedCCSources(refDate, ticker, earnings)
        .pipe(
          catchError((err) => {
            console.error(err);
            return throwError(err);
          }),
        )
        .subscribe((response: any) => {
          if (response && response["status"] === "success") {
            const sourceCount =
              response.custom_sourceid_list &&
              response.custom_sourceid_list.length;
            this.showCcNodataModal(!sourceCount);
          }
        });
    }
  };

  getCcVarsForWeb = (): CcVars => {
    const medata = this.appService.superClone(window.MEData);
    const sourcePanel = this.getMEDataPanel(medata, "sources");
    const revisionPanel = this.getMEDataPanel(medata, "revisions");

    if (sourcePanel) {
      let brokerlist = [];

      if (
        sourcePanel &&
        sourcePanel.values &&
        sourcePanel.values.length > 2 &&
        sourcePanel.values[2].values
      ) {
        brokerlist = sourcePanel.values[2].values;
      }

      //'previouslyAppliedByDate' is only for handling cases where user settings were saved
      //before cc 2.0 was developed and so does not contain key 'custom_consensus_mode'
      //usage 'previouslyAppliedByDate' can safely be reomoved from below line in future
      //ref bug: CACHE-3181
      const previouslyAppliedByDate = !!sourcePanel.date_custom_consensus;
      if (
        sourcePanel.custom_consensus_mode === "byDate" ||
        previouslyAppliedByDate
      ) {
        brokerlist.forEach((broker) => {
          broker.selected_cc = false;
        });
      }

      const ccVars: CcVars = {
        brokerlist: brokerlist,
        showOnlyCurrentCc: sourcePanel.show_only_current,
        sortColumn: sourcePanel.sort_column,
        sortDirection: sourcePanel.sort_order,
        customConsensusDateType:
          sourcePanel.custom_consensus_date_type || "custom",
        customConsensusBrokerType:
          sourcePanel.custom_consensus_mode &&
          (sourcePanel.excl_reaffirm_broker_consensus === false ||
            sourcePanel.excl_reaffirm_broker_consensus === "") &&
          sourcePanel.custom_consensus_mode !== "byDate"
            ? "brokerlist"
            : "reaffirm",
        dateCustomConsensus:
          sourcePanel.custom_consensus_date_type === "relative"
            ? null
            : sourcePanel.date_custom_consensus,
        relativeDays: sourcePanel.relative_days,
        customConsensusMode: sourcePanel.custom_consensus_mode || "byDate",
        revisionsAvailableFrom: revisionPanel.effective_start,
        ticker: medata.ticker,
        includeNonCurrentBrokers: !sourcePanel.inc_non_current_brokers,

        includeNonentitledCurrent: sourcePanel.selected_cc_not_entitled,

        nonentitledCurrent: sourcePanel.non_entitled,
        nonentitledCurrentBrokers: sourcePanel.non_entitled_brokers,

        includeNonentitledNoncurrent: sourcePanel.selected_excl_cc_not_entitled,
        nonentitledNoncurrent: sourcePanel.excl_non_entitled,
        nonentitledNonCurrentBrokers: sourcePanel.excl_non_entitled_brokers,
      };

      if (ccVars.sortDirection) {
        //adding end at the end of received 'asc', 'desc' -> 'ascend', 'descend'
        //this is beacause ant lib sort values accepted are 'ascend' | 'descend' | null
        //but our backend stores the short form 'asc', 'desc' in source medata
        ccVars.sortDirection = `${ccVars.sortDirection}end`;
      }

      if (sourcePanel.inc_non_current_brokers === undefined) {
        ccVars.includeNonCurrentBrokers = false;
      }

      if (typeof ccVars.nonentitledCurrentBrokers === "string") {
        if (ccVars.nonentitledCurrentBrokers !== "") {
          ccVars.nonentitledCurrentBrokers = [ccVars.nonentitledCurrentBrokers];
        } else {
          ccVars.nonentitledCurrentBrokers = [];
        }
      }
      if (typeof ccVars.nonentitledNonCurrentBrokers === "string") {
        if (ccVars.nonentitledNonCurrentBrokers !== "") {
          ccVars.nonentitledNonCurrentBrokers = [
            ccVars.nonentitledNonCurrentBrokers,
          ];
        } else {
          ccVars.nonentitledNonCurrentBrokers = [];
        }
      }

      return ccVars;
    }
    return undefined;
  };

  getMEDataPanel = (medata, panelname) => {
    const panels = medata.panels.filter(
      (panel) => panel.name.toLowerCase() === panelname.toLowerCase(),
    );
    return panels[0];
  };

  getTimeBasedCCSources(refdate: string, ticker: string, earnings: number) {
    return this.http.get(
      `${this.apiUrl}/api/get_time_based_custom_consensus_sources/?refdate=${refdate}&ticker=${ticker}&earnings=${earnings}`,
    );
  }

  getReaffirmedCCSources(ticker: string, pg: string, cut: string) {
    return this.http.get(
      `${this.apiUrl}/api/get_reaffirm_excl_consensus_sources/?ticker=${ticker}&pg=${pg}&cut=${cut}`,
    );
  }

  logUserAction(feature, extras, event = {}) {
    this.appService.logUserAction(
      event,
      window.MEData.ticker,
      window.MEData.view_code,
      feature,
      extras,
      "",
      "",
    );
  }
}
